import type { TFunction } from 'i18next';

import { USAGE_LIMITS_I18N } from 'features/usage-config/constants/usage-meters';
import {
  IGetAgentModelItem,
  IModelRef,
  IModelRestriction,
} from 'features/usage-config/types/usage-enforcement.model';

const T = USAGE_LIMITS_I18N;

export interface ModelGroup {
  key: string;
  label: string;
  emptyNote?: string;
  models: IGetAgentModelItem[];
}

/** A Blocks model is named by its model name, a custom one by its id. */
export const refOf = (model: IGetAgentModelItem): IModelRef =>
  model.is_system === false
    ? { model_id: model.id ?? '', is_system: false }
    : { model_id: model.model_name, is_system: true };

export const refKey = (ref: IModelRef) => `${ref.is_system}:${ref.model_id}`;

/** Every model while the restriction is off; only the picked ones while it is on. */
export const allowedByRestriction = (
  models: IGetAgentModelItem[],
  restriction: IModelRestriction
): IGetAgentModelItem[] => {
  if (!restriction.enabled) return models;
  const picked = new Set(restriction.models.map(refKey));
  return models.filter((model) => picked.has(refKey(refOf(model))));
};

/**
 * Blocks models by type, then the tenant's own, kept apart the way the agent's picker
 * keeps them, each model once. `customShown` says which custom models may be offered.
 */
export const groupModels = (
  models: IGetAgentModelItem[],
  customShown: (model: IGetAgentModelItem) => boolean,
  customEmptyNote: string,
  t: TFunction
): ModelGroup[] => {
  const named = models.filter((model) => model.model_name);
  const distinct = (list: IGetAgentModelItem[]) => [
    ...new Map(list.map((model) => [refKey(refOf(model)), model])).values(),
  ];
  const system = named.filter((model) => model.is_system !== false);
  const types = [
    ...new Set([...Object.keys(T.MODEL_TYPE_LABELS), ...system.map((model) => model.model_type)]),
  ];
  return [
    ...types
      .map((type) => ({
        key: `system-${type}`,
        // An unknown type has no key; t() hands the raw type back unchanged.
        label: t(T.MODELS_GROUP_BLOCKS_OF, { type: t(T.MODEL_TYPE_LABELS[type] ?? type) }),
        models: distinct(system.filter((model) => model.model_type === type)),
      }))
      .filter((group) => group.models.length),
    {
      key: 'custom',
      label: t(T.MODELS_GROUP_CUSTOM),
      emptyNote: customEmptyNote,
      models: distinct(named.filter((model) => model.is_system === false && customShown(model))),
    },
  ];
};
